import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import LoadingSpinner from '../common/LoadingSpinner'
import { nsConfirm } from '../shared/ns-confirm'
import { NSField, NSSelect, NSToggle, NSPrefix } from '../shared/ns-kit'
import { I, Icon } from '../shared/ns-icons'

/* Client Settings — ported 1:1 from the prototype's NSClientSettings
   (Profile · Security · Brand · Billing · Notifications). Profile/Security/
   Brand/Billing(plan, Stripe key) are wired to the real API; notification prefs
   persist locally (no server endpoint yet); active sessions are display-only. */

const INDUSTRIES = ['Food & Beverage', 'Retail & E-commerce', 'Hospitality', 'Health & Wellness', 'Professional Services', 'Technology', 'Other']

// ── Small layout helpers (match the prototype's NSCardSection / NSSettingRow) ──
function SettingsSection({ title, desc, right, children }) {
  return (
    <div className="card card-pad">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
        <div style={{ flex: 1 }}>
          <h2 className="h2">{title}</h2>
          {desc && <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>{desc}</div>}
        </div>
        {right}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
    </div>
  )
}

function SettingRow({ label, desc, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
        {desc && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.45 }}>{desc}</div>}
      </div>
      {children}
    </div>
  )
}

/* ============================ PAGE ============================ */
const ClientSettings = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const TAB_IDS = ['profile', 'security', 'brand', 'billing', 'notifications']
  const initialTab = TAB_IDS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'profile'
  const [tab, setTab] = useState(initialTab)

  const { data: profile, isLoading } = useQuery('client-profile', () => apiService.get('/clients/profile').then(r => r.data))
  const { data: securityInfo } = useQuery('user-security', () => apiService.get('/auth/security').then(r => r.data), { staleTime: 5 * 60 * 1000 })
  const { data: plansRaw } = useQuery('billing-plans', () => apiService.get('/billing/plans').then(r => r.data), { staleTime: 10 * 60 * 1000 })
  const { data: stripeStatus, refetch: refetchStripe } = useQuery('stripe-status', () => apiService.get('/clients/stripe').then(r => r.data), { staleTime: 60 * 1000 })

  const TABS = [['profile', 'Profile'], ['security', 'Security'], ['brand', 'Brand'], ['billing', 'Billing'], ['notifications', 'Notifications']]

  if (isLoading) {
    return <div style={{ padding: 60 }}><LoadingSpinner size="lg" text="Loading settings…" /></div>
  }

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Your account</div>
          <h1>Settings</h1>
          <div className="subtitle">Manage your profile, security and billing</div>
        </div>
      </div>

      <div className="page-body" style={{ maxWidth: 880 }}>
        <div className="tabs" style={{ alignSelf: 'flex-start' }}>
          {TABS.map(([id, label]) => (
            <button
              key={id}
              className={tab === id ? 'active' : ''}
              onClick={(e) => {
                setTab(id)
                // On phones the strip scrolls horizontally; reveal the tapped tab
                // fully so the last one ("Notifications") isn't clipped by the edge.
                e.currentTarget.scrollIntoView({ inline: 'nearest', block: 'nearest' })
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {tab === 'profile' && <ProfileTab profile={profile} user={user} queryClient={queryClient} />}
          {tab === 'security' && <SecurityTab securityInfo={securityInfo} queryClient={queryClient} />}
          {tab === 'brand' && <BrandTab profile={profile} queryClient={queryClient} />}
          {tab === 'billing' && <BillingTab profile={profile} plansRaw={plansRaw} stripeStatus={stripeStatus} refetchStripe={refetchStripe} navigate={navigate} />}
          {tab === 'notifications' && <NotificationsTab profile={profile} queryClient={queryClient} />}
        </div>
      </div>
    </>
  )
}

/* ============================ PROFILE ============================ */
function ProfileTab({ profile, user, queryClient }) {
  const [form, setForm] = useState({
    company_name: profile?.company_name || '',
    contact_name: user?.name || '',
    phone: profile?.phone || '',
    website: (profile?.domain || '').replace(/^https?:\/\//, ''), // shown bare next to the https:// prefix
    industry: profile?.industry && profile.industry !== 'general' ? profile.industry : INDUSTRIES[0],
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const email = profile?.email || user?.email || ''
  const industryOptions = INDUSTRIES.includes(form.industry) ? INDUSTRIES : [form.industry, ...INDUSTRIES]

  const save = useMutation(
    async () => {
      const host = form.website.trim().replace(/^https?:\/\//, '')
      await apiService.put('/clients/profile', {
        company_name: form.company_name,
        domain: host ? `https://${host}` : '',
        phone: form.phone,
        industry: form.industry,
      })
      if (form.contact_name && form.contact_name !== user?.name) {
        await apiService.put('/users/profile', { name: form.contact_name })
      }
      await apiService.post('/clients/onboarding/update', { itemId: 'about', completed: true }).catch(() => {})
    },
    {
      onSuccess: () => {
        toast.success('Profile saved')
        queryClient.invalidateQueries('client-profile')
        queryClient.invalidateQueries('onboarding-checklist')
      },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to save profile'),
    }
  )

  return (
    <SettingsSection title="Profile" desc="Your business details, shown on invoices and your site footer.">
      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <NSField label="Company name"><input className="input" value={form.company_name} onChange={(e) => set('company_name', e.target.value)} /></NSField>
        <NSField label="Contact name"><input className="input" value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} /></NSField>
        <NSField label="Email" hint="managed by your agency — read only">
          <input className="input" value={email} readOnly style={{ color: 'var(--text-muted)', cursor: 'not-allowed' }} />
        </NSField>
        <NSField label="Phone"><input className="input" value={form.phone} onChange={(e) => set('phone', e.target.value)} /></NSField>
        <NSField label="Website"><NSPrefix prefix="https://" value={form.website} onChange={(e) => set('website', e.target.value)} /></NSField>
        <NSField label="Industry"><NSSelect value={form.industry} options={industryOptions} onChange={(e) => set('industry', e.target.value)} /></NSField>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
        <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isLoading}><I.check /> {save.isLoading ? 'Saving…' : 'Save profile'}</button>
      </div>
    </SettingsSection>
  )
}

/* ============================ BRAND ============================ */
const DEFAULT_BRAND_COLORS = ['#6C5CE7', '#2BD9A6']

function BrandColorField({ label, value, onChange }) {
  return (
    <NSField label={label}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <label style={{ width: 44, height: 38, borderRadius: 10, background: value, border: '1px solid var(--border-strong)', flexShrink: 0, cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
          <input type="color" value={value} onChange={(e) => onChange(e.target.value)} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
        </label>
        <input className="input mono" value={value} onChange={(e) => onChange(e.target.value)} style={{ textTransform: 'uppercase' }} />
      </div>
    </NSField>
  )
}

function BrandTab({ profile, queryClient }) {
  const initialColors = Array.isArray(profile?.brand_colors) && profile.brand_colors.length
    ? [profile.brand_colors[0] || DEFAULT_BRAND_COLORS[0], profile.brand_colors[1] || DEFAULT_BRAND_COLORS[1]]
    : DEFAULT_BRAND_COLORS
  const [colors, setColors] = useState(initialColors)
  const logoInputRef = React.useRef(null)
  const faviconInputRef = React.useRef(null)

  const logo = profile?.logo || null
  const favicon = profile?.favicon || null

  const uploadAsset = useMutation(
    async ({ kind, file }) => {
      const fd = new FormData()
      fd.append(kind, file)
      await apiService.post(`/clients/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      if (kind === 'logo') await apiService.post('/clients/onboarding/update', { itemId: 'logo', completed: true }).catch(() => {})
    },
    {
      onSuccess: (_d, { kind }) => {
        toast.success(`${kind === 'logo' ? 'Logo' : 'Favicon'} uploaded`)
        queryClient.invalidateQueries('client-profile')
        queryClient.invalidateQueries('onboarding-checklist')
      },
      onError: (e) => toast.error(e.response?.data?.error || 'Upload failed'),
    }
  )

  const removeAsset = useMutation(
    (kind) => apiService.put('/clients/profile', { [kind]: null }),
    {
      onSuccess: (_d, kind) => {
        toast.success(`${kind === 'logo' ? 'Logo' : 'Favicon'} removed`)
        queryClient.invalidateQueries('client-profile')
      },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to remove'),
    }
  )

  const saveColors = useMutation(
    async () => {
      await apiService.put('/clients/profile', { brand_colors: colors })
      await apiService.post('/clients/onboarding/update', { itemId: 'brand_color', completed: true }).catch(() => {})
    },
    {
      onSuccess: () => {
        toast.success('Brand colours saved')
        queryClient.invalidateQueries('client-profile')
        queryClient.invalidateQueries('onboarding-checklist')
      },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to save colours'),
    }
  )

  const pick = (ref) => ref.current?.click()
  const onFile = (kind) => (e) => {
    const file = e.target.files?.[0]
    if (file) uploadAsset.mutate({ kind, file })
    e.target.value = ''
  }

  return (
    <>
      <SettingsSection title="Logo" desc="Used in your site header and the WebNest portal. PNG, JPG, GIF or WebP.">
        <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile('logo')} />
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 16, alignItems: 'center' }}>
          <div style={{ height: 110, borderRadius: 12, background: 'var(--bg-2)', border: '1.5px dashed var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 14, overflow: 'hidden' }}>
            {logo
              ? <img src={logo} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              : <span style={{ color: 'var(--text-faint)', fontSize: 12.5 }}>No logo yet</span>}
          </div>
          <div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5 }}>
              A clear, square-ish logo works best. We optimise and resize it automatically.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-sm" onClick={() => pick(logoInputRef)} disabled={uploadAsset.isLoading}>
                <I.upload /> {logo ? 'Replace' : 'Upload'}
              </button>
              {logo && <button className="btn btn-sm" onClick={() => removeAsset.mutate('logo')} disabled={removeAsset.isLoading}>Remove</button>}
            </div>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection title="Favicon" desc="Shown in browser tabs. Square PNG, 32×32 or 64×64.">
        <input ref={faviconInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile('favicon')} />
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 16, alignItems: 'center' }}>
          <div style={{ height: 90, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {favicon
              ? <img src={favicon} alt="Favicon" style={{ width: 44, height: 44, borderRadius: 11, objectFit: 'cover' }} />
              : <span style={{ color: 'var(--text-faint)', fontSize: 11.5 }}>None</span>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm" onClick={() => pick(faviconInputRef)} disabled={uploadAsset.isLoading}>
              <I.upload /> {favicon ? 'Replace' : 'Upload'}
            </button>
            {favicon && <button className="btn btn-sm" onClick={() => removeAsset.mutate('favicon')} disabled={removeAsset.isLoading}>Remove</button>}
          </div>
        </div>
      </SettingsSection>

      <SettingsSection title="Brand colours" desc="Applied across your site and customer emails." right={
        <button className="btn btn-primary" onClick={() => saveColors.mutate()} disabled={saveColors.isLoading}><I.check /> {saveColors.isLoading ? 'Saving…' : 'Save'}</button>
      }>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <BrandColorField label="Primary" value={colors[0]} onChange={(v) => setColors([v, colors[1]])} />
          <BrandColorField label="Secondary" value={colors[1]} onChange={(v) => setColors([colors[0], v])} />
        </div>
      </SettingsSection>
    </>
  )
}

/* ============================ SECURITY ============================ */
function SecurityTab({ securityInfo, queryClient }) {
  const enabled = !!securityInfo?.totp_enabled

  // Password
  const [pw, setPw] = useState({ cur: '', nw: '', cf: '' })
  const [savingPw, setSavingPw] = useState(false)
  const changePassword = async () => {
    if (pw.nw !== pw.cf) return toast.error('New passwords do not match')
    if (pw.nw.length < 6) return toast.error('Password must be at least 6 characters')
    setSavingPw(true)
    try {
      await apiService.put('/auth/password', { currentPassword: pw.cur, newPassword: pw.nw })
      toast.success('Password updated')
      setPw({ cur: '', nw: '', cf: '' })
      queryClient.invalidateQueries('user-security')
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update password')
    } finally { setSavingPw(false) }
  }

  // 2FA inline flow
  const [twoFa, setTwoFa] = useState({ mode: 'idle', qr: '', secret: '', code: '', busy: false })
  const set2 = (p) => setTwoFa(s => ({ ...s, ...p }))
  const startSetup = async () => {
    set2({ busy: true })
    try {
      const r = await apiService.post('/auth/2fa/setup')
      set2({ mode: 'setup', qr: r.data.qrCode, secret: r.data.secret, code: '', busy: false })
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to start 2FA setup'); set2({ busy: false }) }
  }
  const verify2fa = async () => {
    set2({ busy: true })
    try {
      await apiService.post('/auth/2fa/verify', { token: twoFa.code })
      toast.success('Two-factor authentication enabled')
      set2({ mode: 'idle', qr: '', secret: '', code: '', busy: false })
      queryClient.invalidateQueries('user-security')
    } catch (e) { toast.error(e.response?.data?.error || 'Invalid verification code'); set2({ busy: false }) }
  }
  const disable2fa = async () => {
    const ok = await nsConfirm({ tone: 'danger', icon: 'shield', title: 'Disable two-factor authentication?', message: 'Your account will be less secure. Enter your current authenticator code to confirm.', confirmLabel: 'Continue' })
    if (!ok) return
    set2({ mode: 'disable', code: '' })
  }
  const confirmDisable = async () => {
    set2({ busy: true })
    try {
      await apiService.post('/auth/2fa/disable', { token: twoFa.code })
      toast.success('Two-factor authentication disabled')
      set2({ mode: 'idle', code: '', busy: false })
      queryClient.invalidateQueries('user-security')
    } catch (e) { toast.error(e.response?.data?.error || 'Invalid authenticator code'); set2({ busy: false }) }
  }

  // Google account linking
  const googleLinked = !!securityInfo?.google_linked
  const googleAvailable = !!securityInfo?.google_available
  const [googleBusy, setGoogleBusy] = useState(false)
  const connectGoogle = async () => {
    setGoogleBusy(true)
    try {
      const r = await apiService.post('/auth/google/link-start')
      window.location.href = r.data.url // hand off to Google's consent screen
    } catch (e) {
      toast.error(e.response?.data?.error || 'Google sign-in is not available')
      setGoogleBusy(false)
    }
  }
  const disconnectGoogle = async () => {
    const ok = await nsConfirm({ tone: 'danger', icon: 'shield', title: 'Disconnect Google?', message: 'You won’t be able to sign in with Google until you reconnect it.', confirmLabel: 'Disconnect' })
    if (!ok) return
    try {
      await apiService.delete('/auth/google/link')
      toast.success('Google account disconnected')
      queryClient.invalidateQueries('user-security')
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to disconnect') }
  }
  // Surface the outcome of a link redirect (?google_link=ok|in_use)
  React.useEffect(() => {
    const p = new URLSearchParams(window.location.search)
    const gl = p.get('google_link')
    if (gl === 'ok') { toast.success('Google account connected'); queryClient.invalidateQueries('user-security') }
    else if (gl === 'in_use') toast.error('That Google account is already linked to another user.')
    if (gl) window.history.replaceState({}, '', window.location.pathname)
  }, [])

  return (
    <>
      <SettingsSection title="Change password" desc="Use 8+ characters with a mix of letters and numbers.">
        <NSField label="Current password"><input className="input" type="password" value={pw.cur} onChange={(e) => setPw(p => ({ ...p, cur: e.target.value }))} placeholder="••••••••••••" /></NSField>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <NSField label="New password"><input className="input" type="password" value={pw.nw} onChange={(e) => setPw(p => ({ ...p, nw: e.target.value }))} placeholder="••••••••••••" /></NSField>
          <NSField label="Confirm new password"><input className="input" type="password" value={pw.cf} onChange={(e) => setPw(p => ({ ...p, cf: e.target.value }))} placeholder="••••••••••••" /></NSField>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={changePassword} disabled={savingPw || !pw.cur || !pw.nw}>{savingPw ? 'Updating…' : 'Update password'}</button>
        </div>
      </SettingsSection>

      <SettingsSection
        title="Two-factor authentication"
        desc="Add a second step at sign-in for extra security."
        right={<span className={`badge badge-${enabled ? 'success' : 'warning'}`}><span className="badge-dot" /> {enabled ? 'On' : 'Off'}</span>}
      >
        {enabled && twoFa.mode !== 'disable' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="icon-tile success" style={{ width: 38, height: 38, fontSize: 17 }}><I.shield /></span>
            <div style={{ flex: 1, fontSize: 13, color: 'var(--text-soft)' }}>Your account is protected with an authenticator app.</div>
            <button className="btn btn-sm" style={{ color: 'var(--danger)' }} onClick={disable2fa}>Disable 2FA</button>
          </div>
        )}

        {enabled && twoFa.mode === 'disable' && (
          <div style={{ display: 'flex', gap: 10, maxWidth: 360 }}>
            <input className="input mono" placeholder="000 000" value={twoFa.code} onChange={(e) => set2({ code: e.target.value.replace(/\D/g, '').slice(0, 6) })} style={{ letterSpacing: 4, textAlign: 'center' }} />
            <button className="btn" style={{ flexShrink: 0, color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={confirmDisable} disabled={twoFa.busy || twoFa.code.length !== 6}>{twoFa.busy ? 'Disabling…' : 'Confirm disable'}</button>
          </div>
        )}

        {!enabled && twoFa.mode !== 'setup' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, fontSize: 13, color: 'var(--text-soft)', lineHeight: 1.6 }}>Protect your account with a code from an authenticator app (Google Authenticator, 1Password, Authy).</div>
            <button className="btn btn-primary" style={{ flexShrink: 0 }} onClick={startSetup} disabled={twoFa.busy}>{twoFa.busy ? 'Loading…' : 'Set up 2FA'}</button>
          </div>
        )}

        {!enabled && twoFa.mode === 'setup' && (
          <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 20, alignItems: 'center' }}>
            <div style={{ width: 150, height: 150, borderRadius: 12, background: 'white', padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {twoFa.qr ? <img src={twoFa.qr} alt="2FA QR code" style={{ width: '100%', height: '100%' }} /> : <LoadingSpinner size="sm" />}
            </div>
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-soft)', lineHeight: 1.6 }}>Scan this QR code with your authenticator app, then enter the 6-digit code to finish.</div>
              {twoFa.secret && <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 8 }}>Manual key: <code style={{ fontFamily: 'var(--font-mono)' }}>{twoFa.secret}</code></div>}
              <div style={{ display: 'flex', gap: 10, marginTop: 14, maxWidth: 320 }}>
                <input className="input mono" placeholder="000 000" value={twoFa.code} onChange={(e) => set2({ code: e.target.value.replace(/\D/g, '').slice(0, 6) })} style={{ letterSpacing: 4, textAlign: 'center' }} />
                <button className="btn btn-primary" style={{ flexShrink: 0 }} onClick={verify2fa} disabled={twoFa.busy || twoFa.code.length !== 6}>{twoFa.busy ? 'Verifying…' : 'Enable 2FA'}</button>
              </div>
              <button className="btn btn-sm" style={{ marginTop: 10 }} onClick={() => set2({ mode: 'idle' })}>Cancel</button>
            </div>
          </div>
        )}
      </SettingsSection>

      <SettingsSection
        title="Connected accounts"
        desc="Link a Google account for one-click sign-in."
        right={<span className={`badge badge-${googleLinked ? 'success' : 'gray'}`}><span className="badge-dot" /> {googleLinked ? 'Connected' : 'Not connected'}</span>}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface)', border: '1px solid var(--border)', flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"/>
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/>
            </svg>
          </span>
          <div style={{ flex: 1, fontSize: 13, color: 'var(--text-soft)', lineHeight: 1.6 }}>
            {googleLinked
              ? 'Your Google account is connected — use “Continue with Google” at sign-in.'
              : 'Connect your Google account to sign in with one click.'}
          </div>
          {!googleAvailable ? (
            <span style={{ fontSize: 12, color: 'var(--text-faint)', flexShrink: 0 }}>Unavailable</span>
          ) : googleLinked ? (
            <button className="btn btn-sm" style={{ flexShrink: 0, color: 'var(--danger)' }} onClick={disconnectGoogle}>Disconnect</button>
          ) : (
            <button className="btn btn-sm" style={{ flexShrink: 0 }} onClick={connectGoogle} disabled={googleBusy}>{googleBusy ? 'Connecting…' : 'Connect'}</button>
          )}
        </div>
      </SettingsSection>

      <SettingsSection title="Active sessions" desc="Devices currently signed in to your account.">
        <SettingRow
          label={<span style={{ display: 'flex', alignItems: 'center', gap: 9 }}><Icon name="desktop" style={{ color: 'var(--text-muted)' }} /> This device <span className="badge badge-success" style={{ fontSize: 10 }}>Current</span></span>}
          desc="Active now"
        />
        <div style={{ fontSize: 12, color: 'var(--text-faint)', paddingTop: 4 }}>Detailed session management across all your devices is coming soon.</div>
      </SettingsSection>
    </>
  )
}

/* ============================ BILLING ============================ */
function BillingTab({ profile, plansRaw, stripeStatus, refetchStripe, navigate }) {
  const planKey = profile?.subscription_plan || 'basic'
  const planName = plansRaw?.[planKey]?.name || planKey.charAt(0).toUpperCase() + planKey.slice(1)
  const planPrice = plansRaw?.[planKey]?.price
  const used = profile?.edits_used ?? 0
  const quota = profile?.edit_quota || 50
  const pct = Math.min(100, Math.round((used / quota) * 100))

  // Client's own Stripe key (kept from the existing settings — not in the prototype)
  const [stripeKey, setStripeKey] = useState('')
  const [showKey, setShowKey] = useState(false)
  const saveStripe = useMutation(
    (key) => apiService.put('/clients/stripe', { stripeSecretKey: key || null }),
    {
      onSuccess: () => { toast.success(stripeKey ? 'Stripe key saved' : 'Stripe key removed'); setStripeKey(''); refetchStripe() },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to save Stripe key'),
    }
  )
  const removeStripe = async () => {
    const ok = await nsConfirm({ tone: 'danger', icon: 'trash', title: 'Remove your Stripe key?', message: 'Card payments will stop working on your sites until you add a new key.', confirmLabel: 'Remove key' })
    if (ok) saveStripe.mutate('')
  }

  return (
    <>
      <SettingsSection title="Current plan" desc="Your subscription with your agency."
        right={<button className="btn btn-sm btn-primary" onClick={() => navigate('/client/billing')}><I.bolt /> Upgrade</button>}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}>{planName}</span>
          <span className="badge badge-accent">Current</span>
        </div>
        {planPrice != null && (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-soft)' }}>£{planPrice}</span>/month
          </div>
        )}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 7 }}>
            <span style={{ color: 'var(--text-soft)' }}>Edit requests this month</span>
            <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{used} / {quota}</span>
          </div>
          <div className="progress" style={{ height: 7 }}><div style={{ width: `${pct}%` }} /></div>
        </div>
      </SettingsSection>

      <SettingsSection title="Payment & invoices" desc="Manage your card and download receipts."
        right={<button className="btn btn-sm" onClick={() => navigate('/client/billing')}>Open Billing</button>}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, border: '1px solid var(--border)', borderRadius: 12, background: 'var(--surface)' }}>
          <span className="icon-tile" style={{ width: 38, height: 38, fontSize: 17 }}><I.card /></span>
          <div style={{ flex: 1, fontSize: 13, color: 'var(--text-soft)' }}>Your payment method, plan changes and invoice history live on the Billing page.</div>
          <button className="btn btn-sm btn-primary" onClick={() => navigate('/client/billing')}>Go to Billing</button>
        </div>
      </SettingsSection>

      <SettingsSection title="Online payments (Stripe)" desc="Connect your own Stripe to take card payments on your website."
        right={<span className={`badge badge-${stripeStatus?.configured ? 'success' : ''}`}>{stripeStatus?.configured ? <><span className="badge-dot" /> Connected</> : 'Not configured'}</span>}>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Enter your Stripe secret key to accept card payments on your site. It's stored securely and never shown again after saving.{' '}
          <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-3)', textDecoration: 'underline' }}>Find your key →</a>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <input
              className="input mono"
              type={showKey ? 'text' : 'password'}
              value={stripeKey}
              onChange={(e) => setStripeKey(e.target.value)}
              placeholder={stripeStatus?.configured ? '••••••••••• (leave blank to keep current)' : 'sk_live_… or sk_test_…'}
              style={{ paddingRight: 38, width: '100%' }}
            />
            <button type="button" onClick={() => setShowKey(v => !v)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)', padding: 0, display: 'inline-flex' }}>
              <I.eye />
            </button>
          </div>
          <button className="btn btn-primary" onClick={() => saveStripe.mutate(stripeKey)} disabled={saveStripe.isLoading || (!stripeKey && !stripeStatus?.configured)}>{saveStripe.isLoading ? 'Saving…' : 'Save'}</button>
          {stripeStatus?.configured && <button className="btn" onClick={removeStripe} disabled={saveStripe.isLoading}>Remove</button>}
        </div>
      </SettingsSection>
    </>
  )
}

/* ============================ NOTIFICATIONS ============================ */
const NOTIF_DEFAULTS = { email: true, requests: true, billing: true, digest: false }
const NOTIF_DEFS = [
  { key: 'email', l: 'Email notifications', d: 'A master switch for all email from WebNest.' },
  { key: 'requests', l: 'Request updates', d: 'When your team replies to or completes a request.' },
  { key: 'billing', l: 'Billing alerts', d: 'Upcoming renewals, receipts and payment issues.' },
  { key: 'digest', l: 'Weekly digest', d: 'A Monday summary of traffic and activity on your site.' },
]

function NotificationsTab({ profile, queryClient }) {
  const [prefs, setPrefs] = useState({ ...NOTIF_DEFAULTS, ...(profile?.notification_prefs || {}) })

  const save = useMutation(
    (next) => apiService.put('/clients/notification-prefs', next),
    {
      onSuccess: (_res, next) => {
        toast.success('Notification preferences saved')
        queryClient.setQueryData('client-profile', (old) => old ? { ...old, notification_prefs: next } : old)
      },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to save preferences'),
    }
  )

  const toggle = (key) => {
    const prev = prefs
    const next = { ...prefs, [key]: !prefs[key] }
    setPrefs(next) // optimistic
    save.mutate(next, { onError: () => setPrefs(prev) }) // roll back on failure
  }

  // The master switch off → the others are implicitly off.
  const masterOff = !prefs.email

  return (
    <SettingsSection title="Notifications" desc="Choose what WebNest emails you about.">
      {NOTIF_DEFS.map(n => {
        const disabled = masterOff && n.key !== 'email'
        return (
          <SettingRow key={n.key} label={n.l} desc={n.d}>
            <span style={disabled ? { opacity: 0.4, pointerEvents: 'none' } : undefined}>
              <NSToggle on={disabled ? false : prefs[n.key]} onChange={() => toggle(n.key)} />
            </span>
          </SettingRow>
        )
      })}
    </SettingsSection>
  )
}

export default ClientSettings
